/**
 * duesState — SINGLE SOURCE for the three-way dues lifecycle wording (C6b ·
 * FE-6, copy-deck §5.1): OPEN (remaining due, optionally part-collected) /
 * CLOSED (settled by collection or by a B4 correction) / WRITTEN_OFF. Reads
 * ONLY the admission's own server-stamped fields (`dues_status`, `dues_paise`,
 * `dues_collected_paise`, `written_off_paise`, `dues_settled_at`,
 * `dues_written_off_at`) — never re-derived per surface. Both the desk-row
 * chip (`AdmissionRow` collapsed row) and the expanded detail line consume
 * THIS one selector, so they can never disagree or show the same word for
 * settled vs written-off.
 *
 * Backward-compat: rows discharged before `dues_status` existed on the wire
 * carry `dues_paise > 0` with no `dues_status` at all — those are treated as
 * OPEN (the only lifecycle a dues-eligible discharge could be in before this
 * feature), so the pre-existing "Dues ₹X" chip keeps rendering unchanged.
 */

import { DUES_STATUS } from '../../constants/admission';
import { formatPaise, formatIsoDateOnly } from '../../utils/money';

export const DUES_CHIP_TONE = {
  OPEN: 'border-red-400 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
  CLOSED: 'border-emerald-400 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
  WRITTEN_OFF: 'border-slate-400 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
};

/**
 * describeDuesState(admission) →
 *   { kind: 'OPEN'|'CLOSED'|'WRITTEN_OFF'|'NONE', chipLabel, chipTone, detailLines: string[] }
 * `detailLines` is never empty for a non-NONE kind, and OPEN/CLOSED/WRITTEN_OFF
 * never share a chip label or detail wording (copy-deck: "never blank, never
 * null, never the same word for settled vs written-off").
 */
export function describeDuesState(admission) {
  const a = admission || {};
  // admission-access (money withheld): the server nulls every dues amount, so
  // there is nothing to say — NONE, never a "₹0" chip off `Number(null) || 0`.
  if (a.dues_paise == null && a.dues_collected_paise == null && a.written_off_paise == null) {
    return { kind: 'NONE', chipLabel: null, chipTone: null, detailLines: [] };
  }
  const duesPaise = Number(a.dues_paise) || 0;
  const collectedPaise = Number(a.dues_collected_paise) || 0;
  const writtenOffPaise = Number(a.written_off_paise) || 0;

  const isWrittenOff = a.dues_status === DUES_STATUS.WRITTEN_OFF;
  const isClosed = a.dues_status === DUES_STATUS.CLOSED;
  // Pre-C6b rows never got dues_status stamped — a stamped dues_paise with no
  // status (or an explicit OPEN) both read as OPEN.
  const isOpen = !isWrittenOff && !isClosed && duesPaise > 0;

  if (isWrittenOff) {
    const date = formatIsoDateOnly(a.dues_written_off_at);
    const who = a.written_off_by_name || a.written_off_by_role || null;
    const reason = a.written_off_reason || null;
    let line = `${formatPaise(writtenOffPaise)} written off as uncollectable`;
    line += date ? ` on ${date}` : '';
    line += who ? ` by ${who}.` : '.';
    line += reason ? ` Reason: ${reason}.` : '';
    const detailLines = [line];
    if (collectedPaise > 0) detailLines.push(`${formatPaise(collectedPaise)} was collected before the write-off.`);
    return {
      kind: 'WRITTEN_OFF',
      chipLabel: `Written off — ${formatPaise(writtenOffPaise)}`,
      chipTone: DUES_CHIP_TONE.WRITTEN_OFF,
      detailLines,
    };
  }

  if (isClosed) {
    const date = formatIsoDateOnly(a.dues_settled_at);
    let line = 'Dues settled';
    line += date ? ` on ${date}` : '';
    line += ` — ${formatPaise(collectedPaise)} collected in full.`;
    return {
      kind: 'CLOSED',
      chipLabel: 'Dues settled',
      chipTone: DUES_CHIP_TONE.CLOSED,
      detailLines: [line],
    };
  }

  if (isOpen) {
    const detailLines = [`Amount due: ${formatPaise(duesPaise)}`];
    if (collectedPaise > 0) detailLines.push(`${formatPaise(collectedPaise)} received so far`);
    return {
      kind: 'OPEN',
      chipLabel: `Dues ${formatPaise(duesPaise)}`,
      chipTone: DUES_CHIP_TONE.OPEN,
      detailLines,
    };
  }

  return { kind: 'NONE', chipLabel: null, chipTone: null, detailLines: [] };
}
